import React from 'react';

import type { IdPriceListDto } from '@/services/ws-orders/src/main/resources/rest-api';
import { getPriceLists } from '@/services/ws-orders/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY } from 'src/constants';
import type { Option } from 'src/types';

type UsePriceListOptionsReturn = {
  options: Option<IdPriceListDto>[];
  pending: boolean;
};

type UsePriceListOptions = (includeInactive: boolean) => UsePriceListOptionsReturn;

export const usePriceListOptions: UsePriceListOptions = (includeInactive) => {
  const getPriceListsRequest = useRequest({
    apiMethod: getPriceLists,
    params: { includeInactive },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getPriceListsRequest.data$,
    (response) =>
      response?.data.reduce((aggr, item) => {
        if (item.id) {
          return [
            ...aggr,
            {
              label: item.name ?? '',
              value: item.id,
              active: item.active,
            },
          ];
        }
        return aggr;
      }, [] as Option<IdPriceListDto>[]) ?? [],
  );
  const pending = useSubscribe(getPriceListsRequest.pending$);

  return React.useMemo<UsePriceListOptionsReturn>(
    () => ({
      options,
      pending,
    }),
    [options, pending],
  );
};
